import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { Box, Divider, LinearProgress, Tab, Tabs } from '@bymason-private/legacy-pattern-components';
import { Route, useRouter } from '@bymason-private/route-provider';
import {
  DeviceModel,
  useGetDevice,
  useGetDeviceLocation,
  useGetDeviceStatus,
} from '@bymason/controller-api-hooks';
import { useDetectIdleView, useIsViewHidden } from '@bymason/util-hooks';
import {
  ConnectivityInfoView,
  DeviceInfoView,
  PendingActionsView,
  // PeripheralsView,
  SessionEndWarningModal,
  SoftwareInfoView,
  XRayLogStream,
  XRayRemoteControl,
} from '../device-tabs';
import { XRAY_FEATURE_TARGET, useToast } from '@bymason-private/controller-shared-widgets';

export type DeviceSubView = 'info' | 'software' | 'connectivity' | 'pending' | 'remote' | 'logs';
// | 'peripherals';

const DEVICE_POLLING_INTERVAL = 20000; // 20 seconds

const tabViewsDefault: DeviceSubView[] = [
  'info',
  'software',
  'connectivity',
  'pending',
  // 'peripherals',
];
const tabViewsXray: DeviceSubView[] = ['remote', 'logs'];
const getTabViewLabel = (tabView: DeviceSubView) => {
  const labelMap = {
    info: 'Info',
    software: 'Software',
    connectivity: 'Connectivity',
    pending: 'Pending actions',
    remote: 'Remote Control',
    logs: 'Logs',
    // peripherals: 'Peripherals',
  };
  return labelMap[tabView];
};
const getTabIndex = (tabViews: DeviceSubView[], subView: DeviceSubView) =>
  tabViews.indexOf(subView) >= 0 ? tabViews.indexOf(subView) : 0;

interface DeviceViewProps {
  deviceId?: string;
  subView?: DeviceSubView;
  hideXrayTabs?: boolean;
  hideNonXrayTabs?: boolean;
  pendingActionsAsTab?: boolean;
  connectivityViewRoute?: Route;
  deviceViewRoute: Route;
  groupViewRoute: Route;
  projectViewRoute?: Route;
  hideTitleColumns?: boolean;
  onDeviceInfoLoad?: (deviceInfo: DeviceModel) => void;
  /** Normally subView comes from route. This is needed when
   * subView needs to be tracked in external state
   */
  onClickSubView?: (subView: DeviceSubView) => void;
}

export function LegacyDeviceDetailsView({
  deviceId = '',
  subView = 'info',
  hideXrayTabs = false,
  hideNonXrayTabs = false,
  pendingActionsAsTab = false,
  deviceViewRoute,
  connectivityViewRoute,
  groupViewRoute,
  projectViewRoute,
  hideTitleColumns,
  onDeviceInfoLoad,
  onClickSubView,
}: DeviceViewProps) {
  const { authToken = '' } = useAuth();
  const { navigate } = useRouter();
  const [setToastMessage, setToastSeverity] = useToast();

  const [isXrayEnabled, setIsXrayEnabled] = useState(false);
  const [tabViews, setTabViews] = useState<DeviceSubView[]>(
    hideNonXrayTabs
      ? []
      : pendingActionsAsTab
      ? tabViewsDefault
      : tabViewsDefault.filter((t) => t !== 'pending')
  );
  const [activeTab, setActiveTab] = useState(getTabIndex(tabViews, subView));
  const [pendingActiveTab, setPendingActiveTab] = useState(0);
  const [showSessionEndWarningModal, setSessionEndWarningModal] = useState(false);
  const [isLogSessionActive, setIsLogSessionActive] = useState(false);
  const [isRemoteSessionActive, setIsRemoteSessionActive] = useState(false);

  const isViewHidden = useIsViewHidden();
  const [isViewIdle] = useDetectIdleView();
  const [getDeviceInfo, deviceInfo, deviceInfoError, deviceInfoBusy, , , clearDeviceInfo] = useGetDevice(
    authToken
  );
  const [getDeviceStatus, deviceStatus, , deviceStatusBusy, , , clearDeviceStatus] = useGetDeviceStatus(
    authToken
  );
  const [
    getDeviceLocation,
    deviceLocation,
    ,
    deviceLocationBusy,
    ,
    ,
    clearDeviceLocation,
  ] = useGetDeviceLocation(authToken);

  const deviceFamily = useMemo(() => (deviceInfo ? deviceInfo.family : 'searching...'), [deviceInfo]);
  const isXrayCompatible = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      return true;
    }

    const { featurePackage, featureCode } = XRAY_FEATURE_TARGET;
    return Boolean(
      deviceInfo?.os.platformFeatures?.find(
        (pf) => pf.package === featurePackage && pf.features.includes(featureCode)
      )
    );
  }, [deviceInfo?.os.platformFeatures]);

  const deviceName = useMemo(
    () => (deviceInfo && deviceInfo.name ? deviceInfo.name : `Device-${deviceId.substr(0, 8)}`),
    [deviceInfo, deviceId]
  );

  const handleTabChange = useCallback(
    (event: unknown, newTab: number) => {
      if (isRemoteSessionActive || isLogSessionActive) {
        setPendingActiveTab(newTab);
        setSessionEndWarningModal(true);
      } else {
        setActiveTab(newTab);
        const nextSubView = tabViews[newTab] || tabViews[0];

        onClickSubView
          ? onClickSubView(nextSubView)
          : navigate(deviceViewRoute, { deviceId, subView: nextSubView });
      }
    },
    [deviceId, deviceViewRoute, isLogSessionActive, isRemoteSessionActive, navigate, tabViews, onClickSubView]
  );

  const handleActionsButtonClick = useCallback(() => {
    const pendingTab = getTabIndex(tabViews, 'pending');
    if (pendingTab) {
      handleTabChange({}, pendingTab);
    }
  }, [handleTabChange, tabViews]);

  useEffect(() => {
    setActiveTab(getTabIndex(tabViews, subView));
  }, [subView, tabViews]);

  const handleDeviceRefresh = useCallback(() => {
    if (deviceId) {
      getDeviceInfo({ deviceId });
      getDeviceStatus({ deviceId });
      getDeviceLocation({ deviceId });
    }
  }, [deviceId, getDeviceInfo, getDeviceLocation, getDeviceStatus]);

  const handleLogSessionStart = useCallback(() => {
    setIsLogSessionActive(true);
  }, []);

  const handleLogSessionEnd = useCallback(() => {
    setIsLogSessionActive(false);
  }, []);

  const handleRemoteSessionStart = useCallback(() => {
    setIsRemoteSessionActive(true);
  }, []);

  const handleRemoteSessionEnd = useCallback(() => {
    setIsRemoteSessionActive(false);
  }, []);

  const handleSessionEndWarningClose = (proceed = false) => {
    setSessionEndWarningModal(false);
    if (proceed) {
      if (isLogSessionActive) {
        setIsLogSessionActive(false);
      }
      if (isRemoteSessionActive) {
        setIsRemoteSessionActive(false);
      }
      const nextSubView = tabViews[pendingActiveTab] || 'info';
      setActiveTab(pendingActiveTab);
      onClickSubView
        ? onClickSubView(nextSubView)
        : navigate(deviceViewRoute, { deviceId, subView: nextSubView });
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!isViewIdle && !isViewHidden) {
        handleDeviceRefresh();
      }
    }, DEVICE_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isViewIdle, isViewHidden, handleDeviceRefresh]);

  useEffect(() => {
    if (deviceInfoError) {
      setToastMessage(deviceInfoError);
      setToastSeverity('error');
    }
  }, [deviceInfoError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (deviceId) {
      clearDeviceInfo();
      clearDeviceStatus();
      clearDeviceLocation();
      getDeviceInfo({ deviceId });
      getDeviceStatus({ deviceId });
      getDeviceLocation({ deviceId });
    }
  }, [
    clearDeviceInfo,
    clearDeviceLocation,
    clearDeviceStatus,
    deviceId,
    getDeviceInfo,
    getDeviceLocation,
    getDeviceStatus,
  ]);

  useEffect(() => {
    if (onDeviceInfoLoad && deviceInfo) {
      onDeviceInfoLoad(deviceInfo);
    }
  }, [deviceInfo, onDeviceInfoLoad]);

  useEffect(() => {
    const runXrayFlagCheck = async () => {
      setIsXrayEnabled(isXrayCompatible);
    };

    if (!hideXrayTabs) {
      runXrayFlagCheck();
    }
  }, [isXrayCompatible, hideXrayTabs]);

  useEffect(() => {
    if (isXrayEnabled && !hideXrayTabs) {
      const tabViewsWithXray = hideNonXrayTabs ? tabViewsXray : [...tabViewsDefault, ...tabViewsXray];
      setTabViews(pendingActionsAsTab ? tabViewsWithXray : tabViewsWithXray.filter((t) => t !== 'pending'));
    }
  }, [isXrayEnabled, hideXrayTabs, hideNonXrayTabs, pendingActionsAsTab]);

  return (
    <Box position='relative'>
      <Box mt={1}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabViews.map((tab) => {
            const pendingCount = deviceStatus?.pendingNotifications?.length;
            return (
              <Tab
                key={tab}
                label={
                  getTabViewLabel(tab) + `${tab === 'pending' && pendingCount ? ` (${pendingCount})` : ''}`
                }
              />
            );
          })}
        </Tabs>
        <Divider sx={{ mt: 0 }} />
        <Box minHeight={4}>
          {(deviceInfoBusy || deviceStatusBusy || deviceLocationBusy) && <LinearProgress />}
        </Box>
      </Box>

      {!!deviceInfo && (
        <div>
          {subView === 'info' && (
            <DeviceInfoView
              deviceInfo={deviceInfo}
              deviceStatus={deviceStatus}
              deviceLocation={deviceLocation}
              refreshBusy={deviceInfoBusy}
              groupViewRoute={groupViewRoute}
              hideTitleColumn={hideTitleColumns}
              pendingActionsAsTab={pendingActionsAsTab}
              onNavToPendingTab={handleActionsButtonClick}
              refreshInfo={() => getDeviceInfo({ deviceId })}
            />
          )}
          {subView === 'software' && (
            <SoftwareInfoView
              deviceInfo={deviceInfo}
              projectViewRoute={projectViewRoute}
              hideTitleIcons={hideTitleColumns}
            />
          )}
          {subView === 'connectivity' && (
            <ConnectivityInfoView deviceInfo={deviceInfo} connectivityViewRoute={connectivityViewRoute} />
          )}
          {subView === 'pending' && <PendingActionsView deviceStatus={deviceStatus} />}
          <XRayRemoteControl
            deviceId={deviceId}
            deviceFamily={deviceFamily}
            deviceName={deviceName}
            onSessionEnd={handleRemoteSessionEnd}
            onSessionStart={handleRemoteSessionStart}
            open={subView === 'remote' && isXrayEnabled}
          />
          <XRayLogStream
            deviceId={deviceId}
            deviceFamily={deviceFamily}
            deviceName={deviceName}
            onSessionEnd={handleLogSessionEnd}
            onSessionStart={handleLogSessionStart}
            open={subView === 'logs' && isXrayEnabled}
          />
          {(subView === 'remote' || subView === 'logs') && !isXrayEnabled && <div />}
        </div>
      )}
      <SessionEndWarningModal
        feature={isLogSessionActive ? 'log-stream' : 'remote-control'}
        onClose={handleSessionEndWarningClose}
        open={showSessionEndWarningModal}
      />
    </Box>
  );
}
